import { useState, useRef } from 'react';
import Papa from 'papaparse';
import {
    Box, Typography, Divider,  
    Checkbox, TextField, Button, IconButton,  
    TableContainer, Table, TableHead, TableBody, TableRow, TableCell,  
    Dialog, DialogTitle, DialogContent, DialogActions,        
} from '@mui/material';
import { ArrowUpward, ArrowDownward, FolderOpen, Close } from '@mui/icons-material';
import FlexBetween from 'components/FlexBetween';
import { DEFAULT_COLUMN_CONFIG } from './columnConfig';


const UploadConfigDialog = ({
    open,
    onClose,
    onSave,
    initialConfig,
}) => {
    const [localConfig, setLocalConfig] = useState(initialConfig);
    const [previewRows, setPreviewRows] = useState([]);
    const [previewFileName, setPreviewFileName] = useState('');
    const fileInputRef = useRef(null);

    const handleBrowseFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setPreviewFileName(file.name);
        Papa.parse(file, {
            preview: 31,
            skipEmptyLines: true,
            complete: (results) => setPreviewRows(results.data.slice(0, 31)),
            error: () => setPreviewRows([]),
        });
        e.target.value = '';
    };

    const moveEntry = (i, dir) => {
        setLocalConfig(prev => {
            const next = [...prev];
            const idxA = next[i].index;
            const idxB = next[i + dir].index;
            [next[i], next[i + dir]] = [next[i + dir], next[i]];
            // Col Index stays at its position - only field moves
            next[i]       = { ...next[i],       index: idxA };
            next[i + dir] = { ...next[i + dir], index: idxB };
            return next;
        });
    };

    const toggleEnabled = (i) => {
        setLocalConfig(prev => prev.map((e, j) => j === i ? { ...e, enabled: !e.enabled } : e));
    };

    const changeIndex = (i, value) => {
        const num = parseInt(value, 10);
        if (isNaN(num) || num < 0) return;
        setLocalConfig(prev => prev.map((e, j) => j === i ? { ...e, index: num } : e));
    };

    const previewColCount = previewRows[0]?.length ?? 0;
    const hasPreview = previewRows.length > 0;

    const handleSave = () => {
        onSave(localConfig);
        onClose();
    };


    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle>
                <FlexBetween>
                    <Typography fontWeight="bold">
                        Upload Column Configuration
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1}>
                        {/* CLOSE */}
                        <IconButton
                            aria-label="close"
                            edge="end"
                            onClick={onClose}
                            sx={{ p: 0, 
                                mr: -1,
                                color: 'neutral.dark', 
                                '&:hover': { bgcolor: 'action.hover' } 
                            }}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                </FlexBetween>                
            </DialogTitle>

            <DialogContent dividers>
                {/* Browse sample file */}
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".txt"
                        style={{ display: 'none' }}
                        onChange={handleBrowseFile}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FolderOpen />}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Browse Sample File
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                        {hasPreview
                            ? `${previewFileName} - ${previewColCount} columns, ${previewRows.length} rows`
                            : 'Browse a .txt sample file to preview column values and verify mapping'}
                    </Typography>
                </Box>

                {/* File preview table */}
                {hasPreview && (
                    <>
                        <TableContainer 
                            sx={{ 
                                maxHeight: 160, 
                                mb: 1, 
                                borderRadius: 1,
                                border: '1px solid', 
                                borderColor: 'divider', 
                            }}
                        >
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {Array.from({ length: previewColCount }, (_, ci) => (
                                            <TableCell 
                                                key={ci} 
                                                sx={{ fontSize: '0.7rem', py: 0.5, minWidth: 60 }}
                                            >
                                                {ci}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {previewRows?.map((row, ri) => (
                                        <TableRow key={ri}>
                                            {Array.from({ length: previewColCount }, (_, ci) => (
                                                <TableCell
                                                    key={ci}
                                                    sx={{ 
                                                        fontSize: '0.7rem', 
                                                        py: 0.25, 
                                                        maxWidth: 250, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap' 
                                                    }}
                                                >
                                                    {row[ci] ?? ''}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>                        
                    </>
                )}

                <Divider sx={{ mb: 1 }} />

                {/* Column mapping */}
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                    Column Mapping ({localConfig.filter(e => e.enabled).length} / {localConfig.length} enabled)
                </Typography>

                <TableContainer 
                    sx={{ 
                        maxHeight: 460, 
                        border: '1px solid', 
                        borderColor: 'divider', 
                        borderRadius: 1 
                    }}
                >
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 40, fontWeight: 'bold' }}>#</TableCell>
                                <TableCell sx={{ width: 60, fontWeight: 'bold' }}>Enabled</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                                <TableCell sx={{ width: 110, fontWeight: 'bold' }}>Col Index</TableCell>                                
                                <TableCell sx={{ width: 80, fontWeight: 'bold' }}>Reorder</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {localConfig?.map((entry, i) => {                               
                                return (
                                    <TableRow
                                        key={entry.fieldName}
                                        sx={{
                                            opacity: entry.enabled ? 1 : 0.45,
                                            '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                    >
                                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                            {i + 1}
                                        </TableCell>
                                        <TableCell>
                                            <Checkbox
                                                size="small"
                                                checked={entry.enabled}
                                                onChange={() => toggleEnabled(i)}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem' }}>
                                            <Box>
                                                <Typography variant="body2" fontWeight={entry.enabled ? 500 : 400}>
                                                    {entry.label}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {entry.fieldName}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={entry.index}
                                                onChange={(e) => changeIndex(i, e.target.value)}
                                                slotProps={{
                                                    input: {
                                                        inputProps: {
                                                            min: 0,
                                                            max: 999,
                                                            style: { textAlign: 'center', padding: '4px 4px' },
                                                        },
                                                    },
                                                }}
                                                sx={{ width: 80 }}
                                            />
                                        </TableCell>                                        
                                        <TableCell>
                                            <IconButton 
                                                size="small" 
                                                disabled={i === 0} 
                                                onClick={() => moveEntry(i, -1)}
                                            >
                                                <ArrowUpward fontSize="small" />
                                            </IconButton>
                                            <IconButton 
                                                size="small" 
                                                disabled={i === localConfig.length - 1} 
                                                onClick={() => moveEntry(i, 1)}
                                            >
                                                <ArrowDownward fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions 
                sx={{ 
                    px: 2, 
                    py: 1.5, 
                    justifyContent: 'space-between' 
                }}
            >
                <Button
                    size="small"
                    color="warning"
                    onClick={() => setLocalConfig(DEFAULT_COLUMN_CONFIG)}
                >
                    Reset to Default
                </Button>
                <Box display="flex" gap={1}>
                    <Button variant="outlined" size="small" onClick={handleSave}>
                        Save
                    </Button>
                    <Button variant="outlined" size="small" color="secondary" onClick={onClose}>
                        Cancel
                    </Button>                    
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default UploadConfigDialog;
